import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/coastal-dusk.css';

const SettlingInPage = () => {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(err => console.log('Preview autoplay blocked:', err));
    }
  }, []);

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.webkitEnterFullscreen) {
        videoRef.current.webkitEnterFullscreen();
      } else {
        setIsFullscreen(true);
      }
      
      videoRef.current.play().catch(err => console.error('Failed to play:', err));
    }
  };

  const handleExitFullscreen = () => {
    setIsFullscreen(false);
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.pause();
    }
  };

  const handleVideoEnded = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.log('Preview restart blocked:', err));
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(err => console.log('Preview restart blocked:', err));
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleContinue = () => {
    navigate('/journaling');
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--grey-deep)' }}>
      {/* Blurred Background Image */}
      <div className="landing-bg-image" style={{ backgroundImage: 'url(/assets/landing/bg-coastal.jpg)' }} />
      
      {/* Gradient Orbs */}
      <div className="landing-orb cool focus-shift" style={{ '--orb-opacity': '0.4', top: '10%', left: '-5%', width: '45vw', height: '45vw', maxWidth: '500px', maxHeight: '500px' }} />
      <div className="landing-orb neutral" style={{ '--orb-opacity': '0.3', top: '45%', right: '-5%', width: '40vw', height: '40vw', maxWidth: '450px', maxHeight: '450px', animationDelay: '-5s' }} />

      {/* Logout Button */}
      <div className="absolute top-6 right-6 z-50">
        <button
          onClick={() => {
            localStorage.removeItem('token');
            navigate('/');
          }}
          className="cta-pill-landing"
          style={{ fontSize: '0.82rem', padding: '8px 16px' }}
          title="Your progress is automatically saved"
        >
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 relative z-10">
        {/* Title */}
        <h1 style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', color: 'var(--text-primary)', letterSpacing: '-0.01em', textAlign: 'center', marginBottom: '2rem', lineHeight: 1.2 }}>
          Settle into stillness
        </h1>

        {/* Video Section */}
        <div 
          className={`relative cursor-pointer transition-all duration-300 ${
            isFullscreen 
              ? 'fixed inset-0 z-50 bg-black flex items-center justify-center' 
              : 'w-full max-w-md'
          }`}
          onClick={!isFullscreen ? handleVideoClick : undefined}
        >
          <video
            ref={videoRef}
            src="/assets/page1/settle-in-video.mp4"
            loop={!isFullscreen}
            muted={!isFullscreen}
            playsInline
            controls={isFullscreen}
            onEnded={handleVideoEnded}
            className={`${
              isFullscreen 
                ? 'w-full h-full object-contain' 
                : 'w-full rounded-2xl shadow-2xl'
            }`}
            style={!isFullscreen ? { boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)' } : {}}
          />
          
          {/* Play Overlay */}
          {!isFullscreen && (
            <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl" style={{ background: 'rgba(26, 30, 36, 0.3)' }}>
              <div className="play-circle mb-4" style={{ width: '80px', height: '80px' }}>
                <svg viewBox="0 0 24 24" style={{ width: '28px', height: '28px' }}>
                  <path d="M8 5v14l11-7z" fill="var(--text-primary)"/>
                </svg>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.88rem', color: 'var(--grey-light)' }}>
                Tap to watch full video
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: '0.82rem', color: 'rgba(163, 163, 163, 0.5)', marginTop: '8px' }}>
                or press continue below
              </p>
            </div>
          )}

          {/* Close button in CSS fullscreen mode */}
          {isFullscreen && (
            <button
              onClick={handleExitFullscreen}
              className="lightbox-close"
            >
              <svg fill="none" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" stroke="var(--text-primary)" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Continue Button */}
        {!isFullscreen && (
          <div className="mt-10">
            <button
              onClick={handleContinue}
              className="cta-pill-landing"
            >
              Continue to Journaling
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettlingInPage;
